import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { errText } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Robot, PencilSimple } from "@phosphor-icons/react";

function CorrectionForm({ docId, field, originalValue, onDone }) {
  const [value, setValue] = useState(String(originalValue ?? ""));
  const [comment, setComment] = useState("");
  const [irc, setIrc] = useState("");
  const save = useMutation({
    mutationFn: () => api.post("/review/correction", { document_id: docId, field_adjusted: field, original_value: String(originalValue ?? ""), corrected_value: value, review_comments: comment, tax_code_reference: irc || null, error_category: null }),
    onSuccess: ({ data }) => { toast.success(data.message); onDone(); },
    onError: (e) => toast.error(errText(e)),
  });
  return (
    <div className="mt-1.5 space-y-1.5 rounded-md border border-zinc-200 bg-white p-2" data-testid={`correction-form-${field}`}>
      <Input data-testid={`correction-value-${field}`} className="h-7 text-xs" placeholder="Corrected value" value={value} onChange={(e) => setValue(e.target.value)} />
      <Input data-testid={`correction-comment-${field}`} className="h-7 text-xs" placeholder="Review comment for the preparer (required)" value={comment} onChange={(e) => setComment(e.target.value)} />
      <Input data-testid={`correction-irc-${field}`} className="h-7 text-xs" placeholder="IRC reference (optional, e.g. IRC § 731(a)(1))" value={irc} onChange={(e) => setIrc(e.target.value)} />
      <div className="flex gap-2">
        <Button data-testid={`correction-save-${field}`} size="sm" className="h-6 text-[10px]" disabled={!comment.trim() || !value.trim() || save.isPending} onClick={() => save.mutate()}>Record correction</Button>
        <Button size="sm" variant="ghost" className="h-6 text-[10px]" onClick={onDone}>Cancel</Button>
      </div>
    </div>
  );
}

export function ValidationDesk({ docId, open, onOpenChange }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [editing, setEditing] = useState(null);
  const canCorrect = ["cpa", "partner", "admin"].includes(user?.role);
  const { data } = useQuery({
    queryKey: ["validation", docId],
    queryFn: () => api.get(`/documents/${docId}/validation`).then((r) => r.data),
    enabled: !!docId && open,
  });
  const d = data?.document;
  const base = d ? `${process.env.REACT_APP_BACKEND_URL}/api/documents/${d.id}/file` : null;
  const mime = d?.mime_type || "";
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl" aria-describedby={undefined} data-testid="validation-desk">
        <DialogHeader><DialogTitle className="font-heading text-sm font-mono truncate pr-8">Validation desk — {d?.filename || "…"}</DialogTitle></DialogHeader>
        {d && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="rounded-md border border-zinc-200 bg-zinc-50 overflow-hidden">
              {mime.startsWith("image/") ? (
                <img src={base} alt={d.filename} className="w-full max-h-[60vh] object-contain" />
              ) : mime.includes("pdf") ? (
                <iframe src={base} title={d.filename} className="w-full h-[60vh] bg-white" />
              ) : (
                <p className="text-xs text-zinc-500 p-8 text-center">No inline preview for this file type.</p>
              )}
            </div>
            <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
              <div>
                <p className="text-[10px] uppercase tracking-wide text-zinc-400 mb-1.5">Parsed values — green matched, amber needs eyes</p>
                <div className="space-y-1" data-testid="validation-fields">
                  {(data.fields || []).map((f) => (
                    <div key={f.key}>
                      <div data-testid={`vfield-${f.key}`} className={`flex items-center justify-between text-xs rounded-md border px-2.5 py-1.5 ${f.status === "green" ? "border-emerald-200 bg-emerald-50/50" : "border-amber-200 bg-amber-50/60"}`}>
                        <span className="text-zinc-500">{f.key}</span>
                        <span className="flex items-center gap-1.5">
                          <span className={`font-mono ${f.status === "green" ? "text-emerald-800" : "text-amber-800"}`}>{String(f.value)}</span>
                          {canCorrect && (
                            <button data-testid={`correct-btn-${f.key}`} title="Reviewer correction — logs a Delta Struct and teaches the AI coach"
                              onClick={() => setEditing(editing === f.key ? null : f.key)} className="text-zinc-400 hover:text-zinc-900 transition-colors duration-150">
                              <PencilSimple size={12} />
                            </button>
                          )}
                        </span>
                      </div>
                      {editing === f.key && <CorrectionForm docId={d.id} field={f.key} originalValue={f.value} onDone={() => { setEditing(null); qc.invalidateQueries({ queryKey: ["validation", docId] }); }} />}
                    </div>
                  ))}
                  {(data.fields || []).length === 0 && <p className="text-xs text-zinc-400 italic">No structured fields were extracted from this document.</p>}
                </div>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wide text-zinc-400 mb-1.5 flex items-center gap-1"><Robot size={12} /> AI agent audit trail</p>
                <div className="rounded-md border border-zinc-200 bg-zinc-900 p-3 space-y-1.5" data-testid="validation-agent-log">
                  {(data.agent_log || []).map((l, i) => <p key={i} className="text-[10px] font-mono text-zinc-300 leading-relaxed">→ {l}</p>)}
                </div>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
